import { t } from '../../i18n';
import { createActionButton } from './domFactories';
import { parseTradeSideForReview, splitTradeForReview } from './mailboxData';
import type { MailboxDisplay, TradeGridItem } from './types';
import { ItemGrid } from '../itemGrid/ItemGrid';
import { GridDragDrop } from '../itemGrid/GridDragDrop';
import type { ItemSlot } from '../itemGrid/types';

type TradeReviewItems = { myItems: TradeGridItem[]; theirItems: TradeGridItem[] };

interface RenderTradeComposerParams {
    list: HTMLDivElement;
    tradeTargetName: string;
    tradeMessage: string;
    myItemsGrid: TradeGridItem[];
    myOfferGrid: TradeGridItem[];
    theirItemsGrid: TradeGridItem[];
    theirOfferGrid: TradeGridItem[];
    tradeableOnlyEnabled: boolean;
    onToggleTradeableOnly: () => void;
    requestMoveAmount: (item: TradeGridItem, onConfirm: (amount: number) => void) => void;
    onTradeMessageChange: (message: string) => void;
    onSubmit: () => void;
    onCancel: () => void;
    onMoveFromMyItems: (item: TradeGridItem, amount: number) => void;
    onMoveFromMyOffer: (item: TradeGridItem, amount: number) => void;
    onMoveFromTheirItems: (item: TradeGridItem, amount: number) => void;
    onMoveFromTheirOffer: (item: TradeGridItem, amount: number) => void;
}

function tradeItemToSlot(item: TradeGridItem): ItemSlot {
    return {
        id: item.itemId,
        item: { emoji: item.icon, displayName: item.displayName, quantity: item.quantity },
    };
}

function buildTradeItemGrid(
    gridId: string,
    label: string,
    items: TradeGridItem[],
    onItemClick?: (item: TradeGridItem) => void,
): ItemGrid {
    const capacity = Math.max(items.length + 1, 6);
    const grid = new ItemGrid({
        gridId,
        label,
        capacity,
        slots: items.map(tradeItemToSlot),
    });
    if (onItemClick) {
        grid.onSlotClick = (slotId) => {
            const item = items.find((i) => i.itemId === slotId);
            if (item) onItemClick(item);
        };
    }
    return grid;
}

export function renderTradeComposer(params: RenderTradeComposerParams): void {
    const {
        list,
        tradeTargetName,
        tradeMessage,
        myItemsGrid,
        myOfferGrid,
        theirItemsGrid,
        theirOfferGrid,
        tradeableOnlyEnabled,
        onToggleTradeableOnly,
        requestMoveAmount,
        onTradeMessageChange,
        onSubmit,
        onCancel,
        onMoveFromMyItems,
        onMoveFromMyOffer,
        onMoveFromTheirItems,
        onMoveFromTheirOffer,
    } = params;

    Object.assign(list.style, {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
    });

    const title = document.createElement('div');
    title.textContent = t('userMenu.mailbox.tradeWith', { name: tradeTargetName });
    Object.assign(title.style, {
        color: '#dbeafe',
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: '8px',
    });
    list.appendChild(title);

    const filterRow = document.createElement('div');
    Object.assign(filterRow.style, {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '8px',
        marginBottom: '4px',
    });
    const filterButton = document.createElement('button');
    filterButton.type = 'button';
    filterButton.textContent = 'Tradeable only';
    Object.assign(filterButton.style, {
        border: '1px solid #4b5563',
        borderRadius: '6px',
        padding: '5px 10px',
        cursor: 'pointer',
        fontSize: '0.85rem',
        color: '#e5e7eb',
        backgroundColor: tradeableOnlyEnabled ? '#4b5563' : '#1f2937',
        boxShadow: tradeableOnlyEnabled ? 'inset 0 2px 6px rgba(0,0,0,0.45)' : 'none',
        transform: tradeableOnlyEnabled ? 'translateY(1px)' : 'none',
    });
    filterButton.addEventListener('click', () => {
        onToggleTradeableOnly();
    });
    filterRow.appendChild(filterButton);
    list.appendChild(filterRow);

    const myItemsGridEl = buildTradeItemGrid(
        'my-items',
        t('userMenu.mailbox.grids.myItems'),
        myItemsGrid,
        (item) => requestMoveAmount(item, (amount) => onMoveFromMyItems(item, amount)),
    );
    const myOfferGridEl = buildTradeItemGrid(
        'my-offer',
        t('userMenu.mailbox.grids.myOffer'),
        myOfferGrid,
        (item) => requestMoveAmount(item, (amount) => onMoveFromMyOffer(item, amount)),
    );
    const theirItemsGridEl = buildTradeItemGrid(
        'their-items',
        t('userMenu.mailbox.grids.theirItems'),
        theirItemsGrid,
        (item) => requestMoveAmount(item, (amount) => onMoveFromTheirItems(item, amount)),
    );
    const theirOfferGridEl = buildTradeItemGrid(
        'their-offer',
        t('userMenu.mailbox.grids.theirOffer'),
        theirOfferGrid,
        (item) => requestMoveAmount(item, (amount) => onMoveFromTheirOffer(item, amount)),
    );

    const findItem = (arr: TradeGridItem[], slotId: string) => arr.find((i) => i.itemId === slotId)!;
    const dnd = new GridDragDrop([
        {
            from: 'my-items',
            to: 'my-offer',
            onTransfer: (slotId) => {
                const item = findItem(myItemsGrid, slotId);
                onMoveFromMyItems(item, item.quantity);
            },
        },
        {
            from: 'my-offer',
            to: 'my-items',
            onTransfer: (slotId) => {
                const item = findItem(myOfferGrid, slotId);
                onMoveFromMyOffer(item, item.quantity);
            },
        },
        {
            from: 'their-items',
            to: 'their-offer',
            onTransfer: (slotId) => {
                const item = findItem(theirItemsGrid, slotId);
                onMoveFromTheirItems(item, item.quantity);
            },
        },
        {
            from: 'their-offer',
            to: 'their-items',
            onTransfer: (slotId) => {
                const item = findItem(theirOfferGrid, slotId);
                onMoveFromTheirOffer(item, item.quantity);
            },
        },
    ]);
    dnd.register(myItemsGridEl);
    dnd.register(myOfferGridEl);
    dnd.register(theirItemsGridEl);
    dnd.register(theirOfferGridEl);

    const gridsRow = document.createElement('div');
    Object.assign(gridsRow.style, {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gridTemplateRows: 'repeat(2, minmax(0, 1fr))',
        gap: '8px',
        flex: '1',
        minHeight: '0',
        overflow: 'auto',
    });
    gridsRow.appendChild(myItemsGridEl.element);
    gridsRow.appendChild(myOfferGridEl.element);
    gridsRow.appendChild(theirItemsGridEl.element);
    gridsRow.appendChild(theirOfferGridEl.element);

    list.appendChild(gridsRow);

    const bottomSection = document.createElement('div');
    Object.assign(bottomSection.style, {
        marginTop: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        paddingTop: '8px',
    });

    const messageInput = document.createElement('input');
    messageInput.type = 'text';
    messageInput.placeholder = t('userMenu.mailbox.tradeMessagePlaceholder');
    messageInput.value = tradeMessage;
    Object.assign(messageInput.style, {
        width: '100%',
        marginTop: '10px',
        padding: '8px',
        backgroundColor: '#111827',
        color: '#e5e7eb',
        border: '1px solid #374151',
        borderRadius: '4px',
    });
    messageInput.addEventListener('input', () => {
        onTradeMessageChange(messageInput.value);
    });
    bottomSection.appendChild(messageInput);

    const actionRow = document.createElement('div');
    Object.assign(actionRow.style, {
        display: 'flex',
        gap: '10px',
        marginTop: '10px',
    });

    const acceptBtn = createActionButton(t('userMenu.mailbox.sendTrade'), '#2563eb');
    Object.assign(acceptBtn.style, { flex: '1' });
    acceptBtn.addEventListener('click', onSubmit);

    const cancelBtn = createActionButton(t('userMenu.mailbox.cancelTradeComposer'), '#374151');
    Object.assign(cancelBtn.style, { flex: '1' });
    cancelBtn.addEventListener('click', onCancel);

    actionRow.appendChild(acceptBtn);
    actionRow.appendChild(cancelBtn);
    bottomSection.appendChild(actionRow);
    list.appendChild(bottomSection);
}

interface RenderIncomingTradeReviewModalParams {
    mail: MailboxDisplay;
    items: TradeReviewItems | null;
    onAccept: (mailId: number) => Promise<boolean>;
    onReject: (mailId: number) => Promise<boolean>;
    onClose: () => void;
}

export function renderIncomingTradeReviewModal(params: RenderIncomingTradeReviewModalParams): HTMLDivElement {
    const { mail, items, onAccept, onReject, onClose } = params;
    const { myItems, theirItems } = items ?? splitTradeForReview(mail.trade);
    const actions = document.createElement('div');
    Object.assign(actions.style, {
        display: 'flex',
        gap: '10px',
    });

    const acceptBtn = createActionButton(t('userMenu.mailbox.accept'), '#2563eb');
    Object.assign(acceptBtn.style, { flex: '1' });
    acceptBtn.addEventListener('click', () => {
        void (async () => {
            const ok = await onAccept(mail.mailId);
            if (ok) onClose();
        })();
    });

    const rejectBtn = createActionButton(t('userMenu.mailbox.reject'), '#7c2d12');
    Object.assign(rejectBtn.style, { flex: '1' });
    rejectBtn.addEventListener('click', () => {
        void (async () => {
            const ok = await onReject(mail.mailId);
            if (ok) onClose();
        })();
    });

    actions.appendChild(acceptBtn);
    actions.appendChild(rejectBtn);

    return renderTradeReviewModal({
        title: t('userMenu.mailbox.reviewTitle', { name: mail.fromDisplayName }),
        message: mail.msg,
        myItems,
        theirItems,
        actions,
        onClose,
    });
}

interface RenderOutgoingTradeReviewModalParams {
    mail: MailboxDisplay;
    items: TradeReviewItems | null;
    onClose: () => void;
}

export function renderOutgoingTradeReviewModal(params: RenderOutgoingTradeReviewModalParams): HTMLDivElement {
    const { mail, items, onClose } = params;
    const fallback = (() => {
        const [offeredSide = '', askedSide = ''] = mail.trade.split('|');
        return {
            myItems: parseTradeSideForReview(offeredSide),
            theirItems: parseTradeSideForReview(askedSide),
        };
    })();

    const { myItems, theirItems } = items ?? fallback;
    return renderTradeReviewModal({
        title: t('userMenu.mailbox.tradeWith', { name: mail.toDisplayName }),
        message: mail.msg,
        myItems,
        theirItems,
        onClose,
    });
}

interface RenderTradeReviewModalParams {
    title: string;
    message: string;
    myItems: TradeGridItem[];
    theirItems: TradeGridItem[];
    onClose: () => void;
    actions?: HTMLDivElement;
}

function renderTradeReviewModal(params: RenderTradeReviewModalParams): HTMLDivElement {
    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
        position: 'absolute',
        inset: '0',
        backgroundColor: 'rgba(0, 0, 0, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: '30',
    });

    const panel = document.createElement('div');
    Object.assign(panel.style, {
        width: '86%',
        height: '72%',
        backgroundColor: '#151a24',
        border: '1px solid #2a3142',
        borderRadius: '8px',
        padding: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    });

    const title = document.createElement('div');
    title.textContent = params.title;
    Object.assign(title.style, {
        color: '#dbeafe',
        fontWeight: 'bold',
        textAlign: 'center',
    });
    panel.appendChild(title);

    const row = document.createElement('div');
    Object.assign(row.style, {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: '8px',
        flex: '1',
        minHeight: '0',
    });

    const myReviewGrid = buildTradeItemGrid('review-my-items', t('userMenu.mailbox.grids.myItems'), params.myItems);
    const theirReviewGrid = buildTradeItemGrid('review-their-items', t('userMenu.mailbox.grids.theirItems'), params.theirItems);
    row.appendChild(myReviewGrid.element);
    row.appendChild(theirReviewGrid.element);
    panel.appendChild(row);

    const message = document.createElement('textarea');
    message.value = params.message;
    message.readOnly = true;
    message.disabled = true;
    Object.assign(message.style, {
        width: '100%',
        minHeight: '72px',
        resize: 'none',
        padding: '8px',
        backgroundColor: '#0f1420',
        color: '#9ca3af',
        border: '1px solid #2f3748',
        borderRadius: '4px',
        opacity: '0.9',
    });
    panel.appendChild(message);

    if (params.actions) {
        panel.appendChild(params.actions);
    }

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.textContent = '×';
    Object.assign(closeBtn.style, {
        position: 'absolute',
        right: '10px',
        top: '8px',
        background: 'transparent',
        border: 'none',
        color: '#9ca3af',
        fontSize: '1.3rem',
        cursor: 'pointer',
    });
    closeBtn.addEventListener('click', params.onClose);

    panel.style.position = 'relative';
    panel.appendChild(closeBtn);
    overlay.appendChild(panel);
    return overlay;
}
